const CARD_DURATION = 7500;
const VIEWPORT_THRESHOLD = 0.35;
const VIDEO_PRELOAD_MARGIN = '400px 0px';
const NEXT_VIDEO_WARMUP = 2000;
const VIDEO_EXIT_DURATION = 650;
const VIDEO_EXIT_RATE = 0.25;

export function getNextCategoryIndex(currentIndex, cardCount) {
  if (cardCount <= 0) return 0;
  return (currentIndex + 1) % cardCount;
}

export function getVideoExitRate(progress) {
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const easedProgress = clampedProgress * clampedProgress * (3 - 2 * clampedProgress);
  return 1 - (1 - VIDEO_EXIT_RATE) * easedProgress;
}

class ExpandableCategoryCards {
  constructor(root) {
    this.root = root;
    this.items = [...root.querySelectorAll('[data-category-card-item]')];
    this.activeIndex = Number.parseInt(root.dataset.activeIndex || '0', 10) || 0;
    this.remainingTime = CARD_DURATION;
    this.startedAt = 0;
    this.timeoutId = null;
    this.videoWarmTimer = null;
    this.pauseReasons = new Set(['viewport']);
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopQuery = window.matchMedia('(min-width: 48rem)');
    this.intersectionObserver = null;
    this.preloadObserver = null;
    this.mediaNearViewport = false;
    this.videoExitAnimations = new Map();
    this.listeners = [];
    this.interactionFrames = new Set();
    this.destroyed = false;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionPreference = this.handleMotionPreference.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleEntryComplete = this.handleEntryComplete.bind(this);
  }

  mount() {
    if (this.items.length < 2) return;

    this.root.dataset.categoryCardsReady = 'true';
    // Establish every pause reason before the single initial presentation sync.
    this.root.dataset.motionPreference = this.motionQuery.matches ? 'reduce' : 'no-preference';
    if (!this.desktopQuery.matches) this.pauseReasons.add('breakpoint');
    if (this.motionQuery.matches) this.pauseReasons.add('motion');
    if (document.hidden) this.pauseReasons.add('document');
    const entryState = document.documentElement?.dataset.landingEntryState;
    if (entryState === 'pending' || entryState === 'running' ||
        document.documentElement?.hasAttribute('data-landing-entry-arrival')) {
      this.pauseReasons.add('entry');
      this.listen(document, 'sigma:landing-entry-complete', this.handleEntryComplete);
    }
    this.bindInteractions();
    this.setActive(this.activeIndex, { restart: true });
    this.observeVisibility();
    this.observeMediaProximity();

    this.listen(document, 'visibilitychange', this.handleVisibilityChange);
    this.listen(this.motionQuery, 'change', this.handleMotionPreference);
    this.listen(this.desktopQuery, 'change', this.handleBreakpointChange);
  }

  listen(target, type, handler) {
    target.addEventListener(type, handler);
    this.listeners.push(() => target.removeEventListener(type, handler));
  }

  nextFrame(callback) {
    const id = window.requestAnimationFrame(() => {
      this.interactionFrames.delete(id);
      if (!this.destroyed) callback();
    });
    this.interactionFrames.add(id);
  }

  handleEntryComplete() {
    if (this.destroyed || !this.pauseReasons.delete('entry')) return;
    // Give the first card its full dwell after the hero has settled.
    this.setActive(this.activeIndex, { restart: true });
  }

  bindInteractions() {
    this.items.forEach((item) => {
      const link = item.querySelector('[data-category-card-link]');

      const onFocus = () => {
        if (!this.desktopQuery.matches) return;
        this.nextFrame(() => {
          this.setPauseReason('focus', link.matches(':focus-visible'));
        });
      };
      link?.addEventListener('focus', onFocus);
      if (link) this.listeners.push(() => link.removeEventListener('focus', onFocus));
    });

    this.listen(this.root, 'focusout', () => {
      this.nextFrame(() => {
        if (!this.root.contains(document.activeElement)) {
          this.setPauseReason('focus', false);
        }
      });
    });
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.setPauseReason('viewport', false);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= VIEWPORT_THRESHOLD;
        this.setPauseReason('viewport', !isVisible);
      },
      { threshold: [0, VIEWPORT_THRESHOLD, 1] },
    );
    this.intersectionObserver.observe(this.root);
  }

  observeMediaProximity() {
    if (!('IntersectionObserver' in window)) {
      this.mediaNearViewport = true;
      this.prepareActiveVideo();
      this.syncCardVideos();
      return;
    }

    this.preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        this.mediaNearViewport = true;
        this.prepareActiveVideo();
        this.syncCardVideos();
        this.preloadObserver.disconnect();
      },
      { rootMargin: VIDEO_PRELOAD_MARGIN },
    );
    this.preloadObserver.observe(this.root);
  }

  handleVisibilityChange() {
    this.setPauseReason('document', document.hidden);
  }

  handleMotionPreference(event) {
    const shouldReduceMotion = event.matches;
    this.root.dataset.motionPreference = shouldReduceMotion ? 'reduce' : 'no-preference';
    this.setPauseReason('motion', shouldReduceMotion);
    if (!shouldReduceMotion) this.prepareActiveVideo();
    this.syncPresentation({ restartProgress: !shouldReduceMotion });
  }

  handleBreakpointChange(event) {
    if (event.matches) this.prepareActiveVideo();
    this.setPauseReason('breakpoint', !event.matches);
    this.syncPresentation({ restartProgress: event.matches && !this.motionQuery.matches });
  }

  prepareVideo(index) {
    if (!this.desktopQuery.matches || !this.mediaNearViewport || this.motionQuery.matches ||
        this.destroyed) return;

    const video = this.items[index]?.querySelector('[data-category-card-video]');
    if (!(video instanceof HTMLVideoElement) || video.dataset.preloadReady === 'true') return;

    video.dataset.preloadReady = 'true';
    video.preload = 'auto';
    video.load();
  }

  prepareActiveVideo() {
    this.prepareVideo(this.activeIndex);
  }

  prepareNextVideo() {
    this.prepareVideo(getNextCategoryIndex(this.activeIndex, this.items.length));
  }

  setActive(nextIndex, { restart = true } = {}) {
    if (nextIndex < 0 || nextIndex >= this.items.length) return;
    if (nextIndex === this.activeIndex && !restart) return;

    const outgoingIndex = nextIndex === this.activeIndex ? null : this.activeIndex;

    if (restart && this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    this.activeIndex = nextIndex;
    this.root.dataset.activeIndex = String(nextIndex);
    if (restart) this.remainingTime = CARD_DURATION;

    this.syncPresentation({ restartProgress: restart, outgoingIndex });
    if (this.isPaused()) {
      this.pauseTimer();
    } else {
      this.startTimer();
    }
  }

  syncPresentation({ restartProgress = false, outgoingIndex = null } = {}) {
    const reducedMotion = this.motionQuery.matches;
    const isDesktop = this.desktopQuery.matches;

    this.items.forEach((item, index) => {
      const isActive = index === this.activeIndex;
      const timeline = item.querySelector('[data-category-card-timeline]');

      item.dataset.active = isActive ? 'true' : 'false';
      if (timeline) timeline.hidden = !isDesktop || !isActive || reducedMotion;
      if (!isActive) item.dataset.progressState = 'idle';
    });

    if (restartProgress && isDesktop && !reducedMotion && !this.pauseReasons.has('entry')) {
      const activeItem = this.items[this.activeIndex];
      activeItem.dataset.progressState = 'idle';
      void activeItem.offsetWidth;
      activeItem.dataset.progressState = 'active';
    }

    this.syncAutoplayState();
    this.syncCardVideos({ outgoingIndex });
  }

  syncCardVideos({ outgoingIndex = null } = {}) {
    // Video joins the entrance; the entry pause still holds the rotation timer.
    const canPlay =
      this.mediaNearViewport &&
      this.desktopQuery.matches &&
      !this.motionQuery.matches &&
      !document.hidden &&
      !this.pauseReasons.has('viewport') &&
      !this.destroyed;

    this.items.forEach((item, index) => {
      const video = item.querySelector('[data-category-card-video]');
      if (!(video instanceof HTMLVideoElement)) return;

      if (canPlay && index === this.activeIndex) {
        this.prepareVideo(index);
        this.cancelVideoExit(video);
        video.playbackRate = 1;
        video.dataset.playbackState = 'playing';
        video.play().catch(() => {});
      } else if (canPlay && index === outgoingIndex && !video.paused) {
        video.dataset.playbackState = 'exiting';
        this.easeVideoToPause(video);
      } else {
        this.cancelVideoExit(video);
        this.pauseVideo(video);
      }
    });
  }

  easeVideoToPause(video) {
    this.cancelVideoExit(video);
    const startedAt = performance.now();

    const step = (now) => {
      const progress = (now - startedAt) / VIDEO_EXIT_DURATION;
      video.playbackRate = getVideoExitRate(progress);

      if (progress < 1 && !video.paused) {
        const frameId = window.requestAnimationFrame(step);
        this.videoExitAnimations.set(video, frameId);
        return;
      }

      this.pauseVideo(video);
      this.videoExitAnimations.delete(video);
    };

    const frameId = window.requestAnimationFrame(step);
    this.videoExitAnimations.set(video, frameId);
  }

  cancelVideoExit(video) {
    const frameId = this.videoExitAnimations.get(video);
    if (frameId === undefined) return;

    window.cancelAnimationFrame(frameId);
    this.videoExitAnimations.delete(video);
  }

  pauseVideo(video) {
    video.pause();
    video.playbackRate = 1;
    video.dataset.playbackState = 'paused';
  }

  syncAutoplayState() {
    const paused = this.isPaused();
    const reducedMotion = this.motionQuery.matches;
    this.root.dataset.autoplayState = reducedMotion ? 'reduced' : paused ? 'paused' : 'running';
  }

  setPauseReason(reason, shouldPause) {
    if (shouldPause) {
      this.pauseReasons.add(reason);
    } else {
      this.pauseReasons.delete(reason);
    }

    if (this.isPaused()) {
      this.pauseTimer();
    } else {
      this.startTimer();
    }
    this.syncAutoplayState();
    this.syncCardVideos();
  }

  isPaused() {
    return this.pauseReasons.size > 0;
  }

  startTimer() {
    if (this.timeoutId || this.isPaused() || this.motionQuery.matches || this.destroyed) return;
    if (this.remainingTime <= 0) this.remainingTime = CARD_DURATION;

    this.startedAt = performance.now();
    const warmupDelay = Math.max(0, this.remainingTime - NEXT_VIDEO_WARMUP);
    this.videoWarmTimer = window.setTimeout(() => {
      this.videoWarmTimer = null;
      this.prepareNextVideo();
    }, warmupDelay);
    this.timeoutId = window.setTimeout(() => {
      this.timeoutId = null;
      this.remainingTime = CARD_DURATION;
      this.setActive(getNextCategoryIndex(this.activeIndex, this.items.length), { restart: true });
    }, this.remainingTime);
  }

  destroy() {
    this.destroyed = true;
    this.pauseTimer();
    window.clearTimeout(this.videoWarmTimer);
    this.listeners.forEach((remove) => remove());
    this.listeners = [];
    this.intersectionObserver?.disconnect();
    this.preloadObserver?.disconnect();
    this.interactionFrames.forEach((id) => window.cancelAnimationFrame(id));
    this.interactionFrames.clear();
    this.items.forEach((item) => {
      const video = item.querySelector('[data-category-card-video]');
      if (!(video instanceof HTMLVideoElement)) return;
      this.cancelVideoExit(video);
      this.pauseVideo(video);
    });
  }

  pauseTimer() {
    if (!this.timeoutId) return;

    window.clearTimeout(this.timeoutId);
    window.clearTimeout(this.videoWarmTimer);
    this.timeoutId = null;
    this.videoWarmTimer = null;
    this.remainingTime = Math.max(0, this.remainingTime - (performance.now() - this.startedAt));
  }
}

const mountedCategoryCards = new WeakMap();

function mountExpandableCategoryCards(scope = document) {
  scope.querySelectorAll('[data-expandable-category-cards]').forEach((root) => {
    if (mountedCategoryCards.has(root)) return;
    const cards = new ExpandableCategoryCards(root);
    mountedCategoryCards.set(root, cards);
    cards.mount();
  });
}

if (typeof document !== 'undefined') {
  mountExpandableCategoryCards();
  document.addEventListener('shopify:section:load', (event) => mountExpandableCategoryCards(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-expandable-category-cards]').forEach((root) => {
      mountedCategoryCards.get(root)?.destroy();
      mountedCategoryCards.delete(root);
    });
  });
}
